import React, { useState, useEffect } from 'react';
import { useAppContext } from '@/contexts/AppContext';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import NumericKeypad from '@/components/features/shared/NumericKeypad';
import LockerSelection from '@/components/features/deposit/LockerSelection';
import { calculateAmount } from '@/services';
import LockerMonitor from '../shared/LockerMonitor';
import { useTranslation } from 'react-i18next';

const PersonalDeposit: React.FC = () => {
    const {
        step,
        setStep,
        phone,
        setPhone,
        lockerSize,
        lockerType,
        holdTime,
        amount,
        setAmount,
        setError,
        assignedLocker,
    } = useAppContext();
    const [isLockerClosed, setIsLockerClosed] = useState<boolean>(false);
    const { t } = useTranslation();
    const handleRecipientPhoneEnter = () => {
        if (phone.length < 9) {
            setError('Please enter a valid phone number.');
            return;
        }
        setStep(8);
    };

    const handleLockerSelectionComplete = async () => {
        try {
            if (lockerSize && lockerType && holdTime) {
                const calculatedAmount = await calculateAmount(lockerSize, lockerType, holdTime);
                setAmount(calculatedAmount);
                setStep(8);
            } else {
                setError('Please select locker size, type, and hold time.');
            }
        } catch (error) {
            setError('An error occurred while calculating the amount. Please try again.');
        }
    };

    const handlePaymentConfirmation = () => {
        setStep(9);
    };

    const handleRestart = () => {
        setPhone('');
        setError('');
        setStep(0);
    };

    const handleAnotherDeposit = () => {
        setPhone('');
        setStep(7);
    };
    
    const handleLockerStateChange = (isClosed: boolean) => {
        console.log('Locker state changed:', isClosed); // Add logging
        setIsLockerClosed(isClosed);
    };

    const handlePhoneKeyPress = (key: string) => {
        setPhone(phone + key);
    };

    const renderStep = () => {
        switch (step) {
            case 7:
                return <LockerSelection onComplete={handleLockerSelectionComplete} showHoldTimeInput={true}/>;
            case 71:
                return (
                    <>
                        <h2 className="text-xl font-bold text-center text-foreground">Enter recipient's phone number</h2>
                        <Input
                            type="tel"
                            placeholder="Enter recipient's phone number"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                        <NumericKeypad
                            onKeyPress={handlePhoneKeyPress}
                            onClear={() => setPhone('')}
                            onBackspace={() => setPhone(phone.slice(0, -1))}
                        />
                        <Button onClick={handleRecipientPhoneEnter} variant="outline" size="lg">Confirm</Button>
                    </>
                );
            case 8:
                return (
                    <div className='items-center text-center font-bold'>
                        <p>Parcel for: {phone}</p>
                        <p>Amount to pay: {amount.toFixed(2)} KM</p>
                        <p>Scan this barcode to pay or follow payment instructions on POS</p>
                        <img src="/barcode.gif" alt="Payment Barcode" />
                        <Button onClick={handlePaymentConfirmation} variant="outline" size="lg">Confirm Payment</Button>
                        <Button onClick={() => setStep(7)} variant="outline" size="lg">Change Details</Button>
                    </div>
                );
                case 9:
                    return (
                        <div className='space-y-6'>
                            <h2 className="text-xl font-bold text-center text-foreground">{t('depositComplete')}</h2>
                            <p>{t('recipientPhone')}: {phone}</p>
                            <p>{t('assignedLocker')}:</p>
                            <p className='text-3xl font-bold'>{assignedLocker}</p>
                            <p>{lockerSize} - {lockerType}</p>
                            <p>{t('holdTime')}: {holdTime} {t('days')}</p>
                            <p>{t('amountPaid')}: {amount.toFixed(2)} KM</p>
                            <LockerMonitor 
                                lockerId={assignedLocker} 
                                onStateChange={handleLockerStateChange}
                            />
                            <Button 
                                onClick={handleAnotherDeposit} 
                                variant="outline" 
                                size="lg" 
                                className="w-full"
                                disabled={!isLockerClosed}
                            >
                                {t('anotherDeposit')}
                            </Button>
                            <Button 
                                onClick={handleRestart} 
                                variant="outline" 
                                size="lg" 
                                className="w-full"
                                disabled={!isLockerClosed}
                            >
                                {t('finish')}
                            </Button>
                        </div>
                    );
            default:
                return null;
        }
    };

    return (
        <div className="space-y-4">
            {renderStep()}
            <Button onClick={handleRestart} variant="outline" size="lg">Cancel</Button>
        </div>
    );
};

export default PersonalDeposit;